
import React from "react";
import { Button } from "@/components/ui/button";
import { useTranslation } from 'react-i18next';
import PuttingScoreButtons from "./scoring/PuttingScoreButtons";
import ChippingScoreButtons from "./scoring/ChippingScoreButtons";
import WedgingScoreButtons from "./scoring/WedgingScoreButtons";
import BunkerScoreButtons from "./scoring/BunkerScoreButtons";
import IronScoreButtons from "./scoring/IronScoreButtons";
import FairwayWoodScoreButtons from "./scoring/FairwayWoodScoreButtons";
import DriverScoreButtons from "./scoring/DriverScoreButtons";
import GreenAttack2ZoneScoreButtons from "./scoring/GreenAttack2ZoneScoreButtons";
import PointDisplay from "./scoring/PointDisplay";
import PuttingHelp from "./scoring/PuttingHelp";
import Level1PuttingHelp from "../level1/scoring/PuttingHelp";
import Level2PuttingHelp from "../level2/scoring/PuttingHelp";
import Level1PuttingScoreButtons from "../level1/scoring/PuttingScoreButtons";
import Level2PuttingScoreButtons from "../level2/scoring/PuttingScoreButtons";
import { 
  getFormattedPoint, 
  isWedgingTest, 
  isSpecialPuttsTest, 
  isChippingTest,
  isBunkerTest,
  isIronTest,
  isFairwayWoodTest,
  isDriverTest,
  isPuttingTest,
  isGreenAttack2ZoneTest,
  getCurrentLevel
} from "./scoring/utils";
import type { BallTendency } from "@/hooks/level3/types";

interface ScoringPanelProps {
  testId: string | null;
  currentPoint: string;
  currentAttempt: number;
  maxAttempts: number;
  distance: string;
  onScoreChange: (score: number, tendency?: BallTendency) => void;
  onCancelLastEntry: () => void;
  isChippingTest: boolean;
  currentLevel?: 1 | 2 | 3;
}

const ScoringPanel: React.FC<ScoringPanelProps> = ({
  testId,
  currentPoint,
  currentAttempt,
  maxAttempts,
  distance,
  onScoreChange,
  onCancelLastEntry,
  isChippingTest: propIsChippingTest,
  currentLevel: propCurrentLevel
}) => {
  const { t } = useTranslation('training');
  // Use the provided current level or detect it from the URL
  const currentLevel = propCurrentLevel || getCurrentLevel();
  console.log("ScoringPanel - currentLevel:", currentLevel);
  
  const isPutting = isPuttingTest(testId);
  const isSpecialPutts = isSpecialPuttsTest(testId);
  const isWedging = isWedgingTest(testId);
  const isChipping = isChippingTest(testId) && !isWedging;
  const isBunker = isBunkerTest(testId);
  const isGreenAttack2Zone = isGreenAttack2ZoneTest(testId);
  const isIron = isIronTest(testId);
  const isFairwayWood = isFairwayWoodTest(testId);
  const isDriver = isDriverTest(testId);

  console.log("ScoringPanel - testId:", testId);
  console.log("ScoringPanel - isPutting:", isPutting);
  console.log("ScoringPanel - isBunker:", isBunker);
  console.log("ScoringPanel - isIron:", isIron);
  console.log("ScoringPanel - isFairwayWood:", isFairwayWood);
  console.log("ScoringPanel - isDriver:", isDriver);
  console.log("ScoringPanel - currentPoint:", currentPoint);
  console.log("ScoringPanel - currentAttempt:", currentAttempt);
  console.log("ScoringPanel - maxAttempts:", maxAttempts);

  const renderScoringButtons = () => {
    // Special case for Level 1 putts-plat test (G, H, I points)
    if (currentLevel === 1 && testId === "putts-plat" && ["G", "H", "I"].includes(currentPoint)) {
      return (
        <div className="grid grid-cols-1 gap-2">
          <Button
            onClick={() => onScoreChange(4)}
            className="h-12 font-medium bg-green-500 hover:bg-green-600 text-white"
          >
            {t('level1.scoring.putting.ballIn4pts')}
          </Button>
          <Button
            onClick={() => onScoreChange(1)}
            className="h-12 font-medium bg-yellow-500 hover:bg-yellow-600 text-white"
          >
            {t('level1.scoring.putting.ballAt1m')}
          </Button>
          <Button
            onClick={() => onScoreChange(0)}
            className="h-12 font-medium bg-red-500 hover:bg-red-600 text-white"
          >
            {t('level1.scoring.putting.ballOver1m')}
          </Button>
        </div>
      );
    }
    
    if (isDriver) {
      return <DriverScoreButtons onScoreChange={onScoreChange} />;
    } else if (isFairwayWood) {
      return <FairwayWoodScoreButtons onScoreChange={onScoreChange} />;
    } else if (isGreenAttack2Zone) {
      return <GreenAttack2ZoneScoreButtons onScoreChange={onScoreChange} />;
    } else if (isIron) {
      return <IronScoreButtons onScoreChange={onScoreChange} />;
    } else if (isBunker) {
      return <BunkerScoreButtons onScoreChange={onScoreChange} />;
    } else if (isWedging) {
      return <WedgingScoreButtons onScoreChange={onScoreChange} />;
    } else if (isChipping) {
      return <ChippingScoreButtons onScoreChange={onScoreChange} />;
    } else if (isPutting) {
      if (currentLevel === 1) {
        return <Level1PuttingScoreButtons onScoreChange={onScoreChange} />;
      } else if (currentLevel === 2) {
        return <Level2PuttingScoreButtons onScoreChange={onScoreChange} />;
      } else {
        return <PuttingScoreButtons onScoreChange={onScoreChange} />;
      }
    } else {
      // Fallback to standard putting buttons
      return <PuttingScoreButtons onScoreChange={onScoreChange} />;
    }
  };

  // Déterminer le premier point en fonction du niveau et du test
  const getFirstPointForTest = () => {
    if (currentLevel === 1 && testId === "putts-plat") {
      return 'G';
    }
    
    if (currentLevel === 2 && testId === "putts-plat") {
      return 'D';
    }
    
    if (isDriver) return 'DriverA';
    if (isFairwayWood) return 'FairwayWoodA';
    if (isGreenAttack2Zone) return 'IronGreenAttack100';
    if (isIron) return 'IronA'; 
    if (isBunker) return 'BunkerA';
    if (isWedging) return 'WedgingA';
    if (isChipping) return 'ChipRoulA';
    
    return 'A';
  };

  const firstPoint = getFirstPointForTest();
  const cancelButtonDisabled = currentPoint === firstPoint && currentAttempt === 0;

  const handleCancelClick = () => {
    if (!cancelButtonDisabled) {
      onCancelLastEntry();
    }
  };

  return (
    <div className="bg-white/95 rounded-md shadow-sm p-4 border border-gray-200">
      <PointDisplay 
        currentPoint={currentPoint}
        currentAttempt={currentAttempt}
        maxAttempts={maxAttempts}
        distance={distance}
        isChippingTest={propIsChippingTest || isBunker || isIron || isFairwayWood}
        getFormattedPoint={(point) => getFormattedPoint(point)}
        currentLevel={currentLevel}
      />

      {isPutting && currentLevel === 1 && <Level1PuttingHelp />}
      {isPutting && currentLevel === 2 && <Level2PuttingHelp />}
      {isPutting && currentLevel === 3 && <PuttingHelp />}

      <div className="grid grid-cols-1 gap-2 mb-4">
        {renderScoringButtons()}
        <Button 
          onClick={handleCancelClick} 
          className={`h-12 font-medium ${
            cancelButtonDisabled 
              ? "bg-red-100 text-red-400" 
              : "bg-red-200 hover:bg-red-300 text-red-700"
          } border-0 w-full mt-2`}
          disabled={cancelButtonDisabled}
        >
          {t('interface.cancelLastEntry')}
        </Button>
      </div>
    </div>
  );
};

export default ScoringPanel;
